import { EntityType, Locale } from '@frontend/common';
import { DocumentClient } from '@frontend/document/api';
import { Document, DocumentInfo } from '@frontend/document/types';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const DOCUMENT_RESOLVE_KEY = 'dcm_res';
interface DocumentStore {
    [entity_type: string]: {
        [entity_id: string]: EntityDocumentsService;
    };
}

const DocumentStoreContext = createContext<{
    store: DocumentStore;
    preLoadFiles: (entityType: EntityType, entityIds: string[]) => Promise<void>;
    resolveFile: (entityType: EntityType, entityId: string) => Promise<EntityDocumentsService>;
    /**
     * @deprecated For binary/image retrieval, prefer `useBlobRepository().getBlob(...)`.
     * Keep this for legacy document-based flows only.
     */
    getFiles: (entityType: EntityType, entityId: string) => Promise<EntityDocumentsService>;
} | null>(null);

export const DocumentStoreProvider = (props: { children: React.ReactNode }) => {
    const [store, changeStore] = useState<DocumentStore>({});
    const storeRef = useRef(store);
    const [pending, changePending] = useState<{ entityType: EntityType; entityId: string }[]>([]);
    const [timeoutSet, changeTimeoutSet] = useState<boolean>(false);
    const [timeoutFinished, changeTimeoutFinished] = useState<boolean>(false);

    useEffect(() => {
        return () => {
            for (const entity_type in store) {
                for (const entity_id in store[entity_type]) {
                    store[entity_type][entity_id].revokeURLs();
                }
            }
        };
    }, []);

    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    useEffect(() => {
        if (timeoutFinished) {
            const stored = localStorage.getItem(DOCUMENT_RESOLVE_KEY);
            if (stored != null) {
                changePending(JSON.parse(stored));
                changeTimeoutSet(false);
                changeTimeoutFinished(false);
                localStorage.removeItem(DOCUMENT_RESOLVE_KEY);
            }
        }
    }, [timeoutFinished]);

    useEffect(() => {
        if (pending.length > 0) {
            const toResolve = new Map<EntityType, string[]>();
            pending.forEach((p) => {
                if (!toResolve.has(p.entityType)) {
                    toResolve.set(p.entityType, []);
                }
                toResolve.get(p.entityType)?.push(p.entityId);
            });
            Array.from(toResolve.keys()).forEach((entityType) => {
                const entityIds = toResolve.get(entityType);
                if (entityIds) {
                    preLoadFiles(entityType, entityIds);
                }
            });
            changePending([]);
            changeTimeoutSet(false);
            changeTimeoutFinished(false);
        }
    }, [pending]);

    const updateSelf = (service: EntityDocumentsService, entity_type: EntityType, entity_id: string) => {
        changeStore((store) => {
            if (!store[entity_type]) {
                store[entity_type] = {};
            }
            store[entity_type][entity_id] = service;
            return store;
        });
    };

    const preLoadFiles = async (entityType: EntityType, entityIds: string[]): Promise<void> => {
        const storeCopy = { ...store };
        if (!storeCopy[entityType]) {
            storeCopy[entityType] = {};
        }

        const newDocuments: Document[] = (await DocumentClient.resolveDocuments(entityIds, 'entity_id')).results;
        entityIds.forEach((entityId) => {
            const documents: Document[] = newDocuments.filter((d) => d.entity_id === entityId);
            if (!storeCopy[entityType][entityId]) storeCopy[entityType][entityId] = new EntityDocumentsService(documents, entityType, entityId, updateSelf);
            else {
                storeCopy[entityType][entityId].setDocuments(documents);
            }
        });
        changeStore(storeCopy);
        return;
    };

    const resolveFile = async (entityType: EntityType, entityId: string): Promise<EntityDocumentsService> => {
        if (store[entityType] && store[entityType][entityId]) {
            return Promise.resolve(store[entityType][entityId]);
        }

        if (timeoutSet === false) {
            changeTimeoutSet(true);
            setTimeout(() => {
                changeTimeoutFinished(true);
            }, 1000);
        }
        const stored = localStorage.getItem(DOCUMENT_RESOLVE_KEY);
        if (stored == null) {
            localStorage.setItem(DOCUMENT_RESOLVE_KEY, JSON.stringify([{ entityType, entityId }]));
        } else {
            const parsed = JSON.parse(stored) as { entityType: EntityType; entityId: string }[];
            const exists = parsed.some((item) => item.entityType === entityType && item.entityId === entityId);
            if (!exists) {
                localStorage.setItem(DOCUMENT_RESOLVE_KEY, JSON.stringify([...parsed, { entityType, entityId }]));
            }
        }

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (storeRef.current[entityType] && storeRef.current[entityType][entityId]) {
                    resolve(storeRef.current[entityType][entityId]);
                    clearInterval(interval);
                }
            }, 500);
        });
    };

    const getFiles = async (entityType: EntityType, entityId: string): Promise<EntityDocumentsService> => {
        if (store[entityType] && store[entityType][entityId]) {
            return Promise.resolve(store[entityType][entityId]);
        }
        const documents = (await DocumentClient.fetchDocuments({ entity_type: entityType, entity_id: entityId })).results;
        const service = new EntityDocumentsService(documents, entityType, entityId, updateSelf);
        const storeCopy = { ...store, [entityType]: { ...store[entityType], [entityId]: service } };
        changeStore(storeCopy);
        return Promise.resolve(service);
    };

    return <DocumentStoreContext.Provider value={{ store, preLoadFiles, resolveFile, getFiles }}>{props.children}</DocumentStoreContext.Provider>;
};

/**
 * @deprecated For binary/image retrieval, prefer `useBlobRepository()`.
 * This repository remains available for legacy document-based fallback flows.
 */
export const useDocumentRepository = () => {
    const context = useContext(DocumentStoreContext);
    if (!context) {
        throw new Error('useDocumentRepository must be used within an DocumentStoreContext');
    }
    return context;
};

/**
 * @deprecated Used by legacy document repository media classification.
 * Prefer blob attachment flows via `useBlobRepository()` for new work.
 */
export enum DocType {
    IMAGE,
    VIDEO,
    TEXT
}

export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
export const VIDEO_MIME_TYPES = ['video/mp4', 'video/mpeg', 'video/ogg', 'video/webm', 'video/quicktime'];
export const TEXT_MIME_TYPES = ['text/csv', 'application/xml', 'application/json', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
interface ExtraDocumentInfo extends Document {
    blob?: Blob;
    url?: string;
    url_nl_be?: string;
    url_en_us?: string;
    url_fr_fr?: string;
    url_de_de?: string;
    blob_nl_be?: Blob;
    blob_en_us?: Blob;
    blob_fr_fr?: Blob;
    blob_de_de?: Blob;
}

export class EntityDocumentsService {
    private updateSelf: (service: EntityDocumentsService, entity_type: EntityType, entity_id: string) => void;
    private entityType: EntityType;
    private entityId: string;
    private documents: ExtraDocumentInfo[];
    constructor(
        documents: ExtraDocumentInfo[],
        entity_type: EntityType,
        entity_id: string,
        updateSelf: (service: EntityDocumentsService, entity_type: EntityType, entity_id: string) => void
    ) {
        this.documents = documents;
        this.entityType = entity_type;
        this.entityId = entity_id;
        this.updateSelf = updateSelf;
    }

    public getDocuments(): ExtraDocumentInfo[] {
        return this.documents;
    }

    public setDocuments(documents: Document[]) {
        const ids = documents.map((d) => d.id);
        const documentsCopy: ExtraDocumentInfo[] = [...this.documents.filter((d) => ids.includes(d.id))];
        documents.forEach((newDocument) => {
            const found = documentsCopy.find((d) => d.id === newDocument.id);
            if (found) {
                if (found.update_timestamp != newDocument.update_timestamp) {
                    const index = documentsCopy.indexOf(found);
                    documentsCopy[index] = newDocument;
                }
            } else documentsCopy.push(newDocument);
        });
        this.documents = documentsCopy;
    }

    revokeURLs(): void {
        this.documents.forEach((document) => {
            if (!!document && !!document.url) {
                URL.revokeObjectURL(document.url);
            }
        });
    }
    revokeURL(documentId: string): void {
        const document = this.documents?.find((f) => f.id === documentId);
        if (!!document) {
            if (!!document.url) URL.revokeObjectURL(document.url);
            if (!!document.url_nl_be) URL.revokeObjectURL(document.url_nl_be);
            if (!!document.url_en_us) URL.revokeObjectURL(document.url_en_us);
            if (!!document.url_fr_fr) URL.revokeObjectURL(document.url_fr_fr);
            if (!!document.url_de_de) URL.revokeObjectURL(document.url_de_de);
        }
    }

    /**
     * @deprecated Use blob metadata from `useBlobRepository().getBlob(...)` for new image/attachment flows.
     */
    public getType(documentId: string, language?: Locale): DocType {
        const document = this.documents?.find((f) => f.id === documentId);
        if (!document) throw new Error('Document not found');
        const info = this.getLanguageDocumentInfo(documentId, language);
        if (!info.mime_type) throw new Error('Document mime type not found');
        if (IMAGE_MIME_TYPES.includes(info.mime_type)) return DocType.IMAGE;
        else if (VIDEO_MIME_TYPES.includes(info.mime_type)) return DocType.VIDEO;
        else if (TEXT_MIME_TYPES.includes(info.mime_type)) return DocType.TEXT;
        throw new Error('Document mime type not supported');
    }

    /**
     * @deprecated For new binary/image flows, prefer `useBlobRepository().getBlob(...)`.
     */
    async getUrl(documentId: string, language?: Locale): Promise<string> {
        const document = this.documents?.find((f) => f.id === documentId);
        if (!document) throw new Error('Document not found');
        const blobKey = language ? (('blob_' + language.toLocaleLowerCase().replace('-', '_')) as keyof ExtraDocumentInfo) : 'blob';
        const urlKey = language ? (('url_' + language.toLocaleLowerCase().replace('-', '_')) as keyof ExtraDocumentInfo) : 'url';
        const url = document[urlKey];
        if (url && typeof url === 'string') return url;
        else {
            const index = this.documents?.findIndex((de) => de.id === document.id);
            const result = await DocumentClient.fetchDocumentFile(document.account_id, document.id, language).then((res) => {
                const url = URL.createObjectURL(res);
                this.documents[index] = { ...document };
                (this.documents[index][urlKey] as string | null | undefined) = url;
                (this.documents[index][blobKey] as Blob | null | undefined) = res;
                return url;
            });
            this.updateSelf(this, this.entityType, this.entityId);
            return result;
        }
    }

    async getSasUrl(documentId: string, language?: Locale): Promise<string> {
        const document = this.documents?.find((f) => f.id === documentId);
        if (!document) throw new Error('Document not found');
        const url = await DocumentClient.fetchDocumentSasUrl(document.account_id, document.id, language);
        return url;
    }

    async getBlob(documentId: string, language?: Locale): Promise<Blob> {
        const document = this.documents?.find((f) => f.id === documentId);
        if (!document) throw new Error('Document not found');
        const blobKey = language ? (('blob_' + language.toLocaleLowerCase().replace('-', '_')) as keyof ExtraDocumentInfo) : 'blob';
        const urlKey = language ? (('url_' + language.toLocaleLowerCase().replace('-', '_')) as keyof ExtraDocumentInfo) : 'url';
        const blob = document[blobKey];
        if (blob && blob instanceof Blob) return blob;
        else {
            const index = this.documents?.findIndex((de) => de.id === document.id);
            const blob = await DocumentClient.fetchDocumentFile(document.account_id, document.id, language).then((res) => {
                const url = URL.createObjectURL(res);
                this.documents[index] = { ...document };
                (this.documents[index][urlKey] as string | null | undefined) = url;
                (this.documents[index][blobKey] as Blob | null | undefined) = res;
                return res;
            });
            this.updateSelf(this, this.entityType, this.entityId);
            return blob;
        }
    }

    public getLanguageDocumentInfo(documentId: string, language?: Locale): DocumentInfo {
        const document = this.documents?.find((f) => f.id === documentId);
        if (!document) throw new Error('Document not found');
        let info = document.document_info;
        if (language) {
            const key = `document_info_${language.toLocaleLowerCase().replace('-', '_')}`;
            const localizedInfo = key in document ? ((document as any)[key] as DocumentInfo | undefined) : undefined;
            if (localizedInfo) {
                info = localizedInfo;
            }
        }
        if (!info) throw new Error('Document info not found');
        return info;
    }
}
